
import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, FileText, ArrowRight } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const NegotiationTactics = () => {
    return (
        <div className="bg-white">
            <SEO
                title="Advanced Sales Negotiation Training | BPO Consultants"
                description="Master contract negotiations. Learn to defend pricing, handle procurement, and trade concessions intelligently."
                keywords="Sales Negotiation, Contract Redlining, BATNA, Pricing Strategy, B2B Negotiation Tactics"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&q=80&w=1200" alt="Negotiation" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Scale size={14} className="inline mr-1" /> Module 3: Deal Structuring
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Advanced <br /> Negotiation Tactics
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Preserve your margins. Close the deal without giving away the farm.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">Never Split the Difference</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            High-stakes negotiation is psychological warfare. We teach the FBI-tested tactics of tactical empathy, mirroring, and labeling to get what you want while making the counterpart feel like they won.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "BATNA Development",
                                    desc: "Best Alternative to a Negotiated Agreement. Knowing when to walk away increases your power."
                                },
                                {
                                    title: "Procurement Defense",
                                    desc: "How to handle professional buyers whose only KPI is to lower your price."
                                },
                                {
                                    title: "Trading Concessions",
                                    desc: "Never give something for nothing. 'If I can do X for you, will you sign by Y?'"
                                },
                                {
                                    title: "Contract Redlining",
                                    desc: "Understanding standard commercial terms so you don't need Legal for every email."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <FileText size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    {/* SGE / FAQ Section */}
                    <FadeIn className="mb-16">
                        <h2 className="text-2xl font-bold text-[#20222D] mb-6">Common Scenarios</h2>
                        <div className="mt-4 space-y-4">
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">"Your competitor is cheaper."</h3>
                                <p className="text-gray-600">We teach you how to pivot to value, risk reduction, and total cost of ownership (TCO).</p>
                            </div>
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">"We need to cut 20% to sign today."</h3>
                                <p className="text-gray-600">Learn to strip scope instead of price, preserving your unit economics.</p>
                            </div>
                        </div>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Close with Confidence</h2>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Start Module <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default NegotiationTactics;
